import type { TLAssetId, TLImageAsset } from "@tldraw/tlschema";

export type UploadReceipt = {
  src: string;
  meta: TLImageAsset["meta"];
  w: number;
  h: number;
};
export type SavedImage = {
  id: TLAssetId;
  name: string;
  file?: File;
  hasFile?: boolean;
  receipt?: UploadReceipt;
};
export interface ImageOutbox {
  list(): Promise<SavedImage[]>;
  get(id: TLAssetId): Promise<SavedImage | undefined>;
  put(image: SavedImage): Promise<void>;
  remove(id: TLAssetId): Promise<void>;
}

// Scope both reads and writes: another signed-in user or board cannot resume these files.
export function createImageOutbox(user: string, room: string): ImageOutbox {
  let opening: Promise<IDBDatabase> | undefined;
  const scope = [user, room];
  function open() {
    return (opening ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open("planroom-image-outbox", 1);
      request.onupgradeneeded = () => {
        const store = request.result.createObjectStore("images", {
          keyPath: "key",
        });
        store.createIndex("scope", "scope");
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          db.close();
          opening = undefined;
        };
        resolve(db);
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () =>
        reject(new Error("Close other Calque tabs and retry."));
    }).catch((error) => {
      opening = undefined;
      throw error;
    }));
  }
  async function transaction<T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("images", mode);
      const request = action(tx.objectStore("images"));
      // Request success is not enough: wait until the bytes have committed.
      tx.oncomplete = () => resolve(request.result);
      tx.onabort = () =>
        reject(
          tx.error ??
            request.error ??
            new Error("Image could not be saved on this device."),
        );
      tx.onerror = () => reject(tx.error ?? request.error);
    });
  }
  return {
    list: async () => {
      const db = await open();
      return new Promise<SavedImage[]>((resolve, reject) => {
        const rows: SavedImage[] = [];
        const tx = db.transaction("images", "readonly");
        const request = tx
          .objectStore("images")
          .index("scope")
          .openCursor(IDBKeyRange.only(scope));
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          const { id, name, receipt, file } = cursor.value as SavedImage;
          // Do not retain source blobs while enumerating recovery work.
          rows.push({ id, name, receipt, hasFile: !!file });
          cursor.continue();
        };
        tx.oncomplete = () => resolve(rows);
        tx.onabort = tx.onerror = () => reject(tx.error ?? request.error);
      });
    },
    get: (id) => transaction("readonly", (store) => store.get([...scope, id])),
    put: async (image) => {
      await transaction("readwrite", (store) =>
        store.put({ ...image, key: [...scope, image.id], scope }),
      );
    },
    remove: async (id) => {
      await transaction("readwrite", (store) => store.delete([...scope, id]));
    },
  };
}
